import { Button, Col, Row } from "react-bootstrap";
import type { RangeOption } from "../../constants/epicLeaderboard";
import type { ApiResource } from "../../hooks/useApiResource";
import type { PkInsights } from "../../types/playerPk";
import PlayerLink from "../common/PlayerLink";
import SkeletonBlock from "../common/SkeletonBlock";
import PkStatTile, { formatRatio } from "./pk/PkStatTile";

type Props = {
  insights: ApiResource<PkInsights>;
  range: RangeOption;
};

// Compact K/D, kill participation, favorite target and most-killed-by tiles.
const PlayerInsightsRow = ({ insights, range }: Props) => {
  const { data, loading, error, reload } = insights;

  if (!data) {
    // Unknown to the PK tables: the stat cards already say so.
    if (error?.status === 404) return null;
    if (error) {
      return (
        <div className="player-muted-note mt-4">
          Couldn't load PK insights.{" "}
          <Button size="sm" variant="link" className="p-0" onClick={reload}>
            Retry
          </Button>
        </div>
      );
    }
    return (
      <Row className="g-3 mt-3" role="status" aria-label="Loading insights">
        {[0, 1, 2, 3].map((i) => (
          <Col key={i} xs={6} md={3}>
            <SkeletonBlock height={92} style={{ borderRadius: 12 }} />
          </Col>
        ))}
      </Row>
    );
  }

  const { summary, opponents } = data;
  const link = (name: string) => (
    <PlayerLink className="pk-link" player={name} range={range} tab="pk" />
  );

  return (
    <Row className={`g-3 mt-3${loading ? " is-refetching" : ""}`}>
      <Col xs={6} md={3}>
        <PkStatTile
          label="K/D"
          value={formatRatio(summary.kdRatio)}
          sub="kills per death"
        />
      </Col>
      <Col xs={6} md={3}>
        <PkStatTile
          label="Kill participation"
          value={formatRatio(summary.killParticipation)}
          sub="(kills + assists) per death"
        />
      </Col>
      <Col xs={6} md={3}>
        <PkStatTile
          label="Favorite target"
          valueClassName="pk-tile-name"
          value={
            opponents.favoriteTarget
              ? link(opponents.favoriteTarget.opponent)
              : "-"
          }
          sub={
            opponents.favoriteTarget
              ? `${opponents.favoriteTarget.kills} ${
                  opponents.favoriteTarget.kills === 1 ? "kill" : "kills"
                }`
              : undefined
          }
        />
      </Col>
      <Col xs={6} md={3}>
        <PkStatTile
          label="Most killed by"
          valueClassName="pk-tile-name"
          value={opponents.nemesis ? link(opponents.nemesis.opponent) : "-"}
          sub={
            opponents.nemesis
              ? `killed you ${opponents.nemesis.deaths} ${
                  opponents.nemesis.deaths === 1 ? "time" : "times"
                }`
              : undefined
          }
        />
      </Col>
    </Row>
  );
};

export default PlayerInsightsRow;
